/**
 * Cila politikë fiskale vlen për një listë page — dhe cilat shkallë tatimore.
 *
 * Deri tani secili ekran e zgjidhte vetë, dhe në tri mënyra të ndryshme:
 *
 *   • `AddList` merrte `fiscalPolitics` dhe përdorte **`data[0]`** — pra rreshtin
 *     e parë sipas id-së, pa e parë fare se cila politikë ishte aktive dhe pa e
 *     parë periudhën e listës.
 *   • `SalariesList` (regjistri) merrte `find(p => p.Active) ?? data[0]`.
 *   • Normat (pension, orë shtesë, sigurim) vinin nga një kërkesë e TRETË, te
 *     `fiscalPoliciesExt`, me `find(Active) || data[0]`.
 *
 * Pasoja praktike: faqja «Politikat Fiskale» shkruan te `fiscalPoliciesExt`, ndaj
 * çelësi «Aktivizo» dhe shkallët e redaktuara aty nuk e prekshin dot llogaritjen
 * — ajo lexonte gjithnjë politikën e parë. Dikush i ndryshonte brezat, ruante,
 * dhe paga dilte njësoj si më parë, pa asnjë gabim në ekran.
 *
 * Këtu ka një rregull të vetëm: politika zgjidhet sipas PERIUDHËS së listës
 * (dita e parë e muajit kundrejt `ValidFrom`/`ValidTo`), me aktiven si rezervë.
 * Data e parë e muajit dhe jo e fundit sepse ligji zbatohet nga rroga e muajit
 * pasues: 08/L-142 hyri në fuqi më 23.08.2024 dhe ATK-ja e aplikoi nga shtatori.
 */

const dstr = (v) => (v ? String(v).slice(0, 10) : '');
const numId = (v) => Number(v?.Id ?? v?.id ?? v);

/** Shkallët mujore sipas Ligjit Nr. 08/L-142 (nga rroga e shtatorit 2024). */
export const KS_BRACKETS = [
  { From: 0,   To: 250,  Tax: 0,  Description: 'E liruar' },
  { From: 250, To: 450,  Tax: 8,  Description: 'Shkalla 1' },
  { From: 450, To: null, Tax: 10, Description: 'Shkalla 2' },
];

/** Shkallët para 08/L-142 — mbeten për listat e periudhave të vjetra. */
export const KS_BRACKETS_LEGACY = [
  { From: 0,   To: 80,   Tax: 0,  Description: 'E liruar' },
  { From: 80,  To: 250,  Tax: 4,  Description: 'Shkalla 1' },
  { From: 250, To: 450,  Tax: 8,  Description: 'Shkalla 2' },
  { From: 450, To: null, Tax: 10, Description: 'Shkalla 3' },
];

/** Rroga e parë me shkallët e reja. */
export const KS_BRACKETS_FROM = '2024-09-01';

/** Dita e parë e periudhës së listës — `YYYY-MM-01`. */
export function periodStart(year, month) {
  const y = Number(year);
  const m = Number(month);
  if (!y || !m) return '';
  return `${y}-${String(m).padStart(2, '0')}-01`;
}

/**
 * Shkallët e ligjit për një periudhë — rezervë kur politika nuk ka brez të
 * konfiguruar. Pa periudhë supozohet ligji në fuqi.
 */
export function lawBrackets(period) {
  return period && period < KS_BRACKETS_FROM ? KS_BRACKETS_LEGACY : KS_BRACKETS;
}

/**
 * Politika që vlen për periudhën.
 *
 * @param {Array} policies — nga `fiscalPoliciesExt`
 * @param {string} period  — `YYYY-MM-01` (nga `periodStart`); bosh = pa periudhë
 */
export function pickFiscalPolicy(policies, period = '') {
  const list = (policies || []).filter(Boolean);
  if (!list.length) return null;

  if (period) {
    const covering = list.filter((p) => {
      const from = dstr(p.ValidFrom ?? p.valid_from);
      const to   = dstr(p.ValidTo   ?? p.valid_to);
      if (from && from > period) return false;
      if (to   && to   < period) return false;
      return true;
    });
    if (covering.length) {
      // Kur mbulojnë disa, fiton ajo që nis më vonë — politika e re e zë vendin
      // e së vjetrës pa qenë nevoja t'i vihet `ValidTo` asaj që zëvendëson.
      return covering.slice().sort((a, b) =>
        dstr(b.ValidFrom ?? b.valid_from).localeCompare(dstr(a.ValidFrom ?? a.valid_from))
      )[0];
    }
  }

  const active = list.find((p) => p.Active ?? p.active);
  if (active) return active;

  return list.slice().sort((a, b) =>
    dstr(b.ValidFrom ?? b.valid_from).localeCompare(dstr(a.ValidFrom ?? a.valid_from))
  )[0];
}

/** Brezat e një politike nga lista e plotë e detajeve. */
export function bracketsOf(details, policyId) {
  const id = numId(policyId);
  if (!id) return [];
  return (details || []).filter(
    (d) => Number(d.FiscalPoliticsId ?? d.fiscalPoliticsId ?? d.fiscal_policy_id) === id
  );
}

/**
 * Zgjedhja e plotë: politika + brezat e saj, me rezervën e ligjit.
 * `ranges` nuk kthehet kurrë bosh — një listë page pa brez do të llogaritej me
 * tatim zero, që është gabimi më i shtrenjtë i mundshëm këtu.
 */
export function resolveFiscal({ policies, details, year, month } = {}) {
  const period = periodStart(year, month);
  const policy = pickFiscalPolicy(policies, period);
  const own    = policy ? bracketsOf(details, policy.Id ?? policy.id) : [];
  return {
    period,
    policy: policy || null,
    ranges: own.length ? own : lawBrackets(period),
    fromLaw: !own.length,
  };
}
